import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, Row } from "react-bootstrap";
import dayjs from "dayjs";
import IRequestBodyProps from "./IRequestBodyProps";
import Status from "../../../enums/Status";
import IDSAR from "../../../models/dsar/IDSAR";
import { Keywords } from "../../keywordsComponent/Keywords";
import FormFieldValidation from "../../../enums/FormFieldValidation";
import LargeFloatingTextField from "../../../components/formFields/largeFloatingText/LargeFloatingTextField";
import DatePicker from "../../../components/formFields/datePicker/DatePicker";
import FloatingTextField from "../../../components/formFields/floatingText/FloatingTextField";
import sarService from "../../../services/sarService";

interface IDSARValidation {
    purpose: FormFieldValidation;
    dataSearchStartDate: FormFieldValidation;
    dataSearchEndDate: FormFieldValidation;
    dataSearchLocations: FormFieldValidation;
    emailSearchStartDate: FormFieldValidation;
    emailSearchEndDate: FormFieldValidation;
    emailSearchAddress: FormFieldValidation;
    keywords: FormFieldValidation;
}

export default function EditorBody(props: IRequestBodyProps) {
    const { t } = useTranslation();
    
    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [request, setRequest] = useState<IDSAR>(props.request);

    const [validation, setValidation] = useState<IDSARValidation>({
        purpose: FormFieldValidation.NOT_VALIDATED,
        dataSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchLocations: FormFieldValidation.NOT_VALIDATED,
        emailSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchAddress: FormFieldValidation.NOT_VALIDATED,
        keywords: FormFieldValidation.NOT_VALIDATED
    });

    const handleInputChange = (prop: keyof IDSAR, value: any) => {
        setRequest(r => ({ ...r, [prop]: value }));
        setValidation(validation => ({ ...validation, [prop]: FormFieldValidation.NOT_VALIDATED }));
    };

    const handleKeywordsChanged = (keywords: string[]) => {
        setRequest(r => ({ ...r, keywords: keywords }));
        setValidation(validation => ({ ...validation, keywords: FormFieldValidation.NOT_VALIDATED }));
    };

    const handleSubmitClicked = async () => {
        const isValid = validate(request, setValidation);
        if (!isValid) {
            return;
        }

        setStatus(Status.SAVING);

        submit(request)
            .then(result => {
                if (!result) {
                    setStatus(Status.SAVE_ERROR);
                }
                 
                setStatus(Status.SAVE_SUCCESS);
                setTimeout(() => window.location.reload(), 750);
            })
            .catch(() => setStatus(Status.SAVE_ERROR));
    };

    const hasValidationErrors = Object.values(validation).some(v => v === FormFieldValidation.INVALID);

    const isReadOnly = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);

    return (
        <Row>
            <Col>
                <p dangerouslySetInnerHTML={{ __html: t("dsar.request.additionalInfoRequested", { infoRequested: request.additionalInformationRequested }) }} />
                <Row className="mb-3">
                    <Col>
                        <h2 className="h4">
                            {t("dsar.request.sections.custodian.title")}
                        </h2>
                        <ul>
                            <li dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.custodian.fields.name", { name: request.custodian }) }} />
                            <li dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.custodian.fields.consent", { consent: request.custodianConsented ? "yes" : "no" }) }} />
                            {
                                !!request.custodianContractStart &&
                                <li dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.custodian.fields.start", { date: dayjs(request.custodianContractStart).format("DD/MM/YYYY") }) }} />
                            }
                            {
                                !!request.custodianContractEnd &&
                                <li dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.custodian.fields.end", { date: dayjs(request.custodianContractEnd).format("DD/MM/YYYY") }) }} />
                            }
                            <li dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.custodian.fields.type", { type: request.custodianContractType }) }} />
                        </ul>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <h2 className="h4">
                            {t("dsar.request.sections.purpose.title")}
                        </h2>
                        <LargeFloatingTextField
                            label={t("dsar.form.sections.searchInfo.fields.purpose.label")}
                            disabled={isReadOnly}
                            onChange={value => handleInputChange("purpose", value)}
                            validation={validation.purpose}
                            type="textarea"
                            value={request.purpose}
                            required={true}
                            height={150}
                            />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <h2 className="h4">
                            {t("dsar.request.sections.searchInfo.title")}
                        </h2>
                        <p dangerouslySetInnerHTML={{ __html: t("dsar.request.sections.searchInfo.type", { type: request.type }) }} />
                        
                        <Row className="gx-3">
                            <Col>
                                <DatePicker
                                    label={t("dsar.form.sections.searchInfo.fields.start.label")}
                                    disabled={isReadOnly}
                                    required={true}
                                    onChange={value => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            handleInputChange("dataSearchStart", value);
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            handleInputChange("emailSearchStart", value);
                                        }
                                    }}
                                    validation={(() => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            return validation.dataSearchStartDate;
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            return validation.emailSearchStartDate;
                                        }
                                    })()!}
                                    value={(() => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            return new Date(request.dataSearchStart);
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            return new Date(request.emailSearchStart);
                                        }
                                    })()!}
                                    />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <DatePicker
                                    label={t("dsar.form.sections.searchInfo.fields.end.label")}
                                    disabled={isReadOnly}
                                    required={true}
                                    onChange={value => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            handleInputChange("dataSearchEnd", value);
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            handleInputChange("emailSearchEnd", value);
                                        }
                                    }}
                                    validation={(() => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            return validation.dataSearchEndDate;
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            return validation.emailSearchEndDate;
                                        }
                                    })()!}
                                    value={(() => {
                                        if (request.type.toLowerCase().startsWith("data")) {
                                            return new Date(request.dataSearchEnd);
                                        } else if (request.type.toLowerCase().startsWith("email")) {
                                            return new Date(request.emailSearchEnd);
                                        }
                                    })()!}
                                    />
                            </Col>
                        </Row>

                        {
                            request.type.toLowerCase().startsWith("data") &&
                            <Row>
                                <Col>
                                    <LargeFloatingTextField
                                        label={t("dsar.form.sections.searchInfo.fields.dataLocations.label")}
                                        disabled={isReadOnly}
                                        onChange={value => handleInputChange("dataSearchLocations", value)}
                                        validation={validation.dataSearchLocations}
                                        type="textarea"
                                        value={request.dataSearchLocations}
                                        height={100}
                                        required={true}
                                        />
                                </Col>
                            </Row>
                        }

                        {
                            request.type.toLowerCase().startsWith("email") &&
                            <Row>
                                <Col>
                                    <FloatingTextField
                                        label={t("dsar.form.sections.searchInfo.fields.emailAddress.label")}
                                        disabled={isReadOnly}
                                        onChange={value => handleInputChange("emailSearchAddress", value)}
                                        validation={validation.emailSearchAddress}
                                        type="email"
                                        value={request.emailSearchAddress}
                                        required={true}
                                        />
                                </Col>
                            </Row>
                        }
                    </Col>
                </Row>
                <Keywords
                    keywords={request.keywords}
                    isReadOnly={isReadOnly}
                    validation={validation.keywords}
                    onKeywordsChanged={handleKeywordsChanged}
                    />

                <Row>
                    <Col>
                        {
                            hasValidationErrors &&
                            <Alert variant="danger">
                                {t("dsar.form.validation")}
                            </Alert>
                        }
                        {
                            status === Status.SAVING &&
                            <Alert variant="info">
                                {t("general.pleaseWait")}
                            </Alert>
                        }
                        {
                            status === Status.SAVE_SUCCESS &&
                            <Alert variant="success">
                                {t("dsar.success.submit")}
                            </Alert>
                        }
                        {
                            status === Status.SAVE_ERROR &&
                            <Alert variant="danger">
                                {t("dsar.errors.submit")}
                            </Alert>
                        }
                    </Col>
                </Row>
                
                <Row className="justify-content-center">
                    <Col xs={12} md={10} lg={6} xl={4}>
                        <div className="d-grid">
                            <Button type="button" size="lg" variant="primary" disabled={isReadOnly} onClick={handleSubmitClicked}>
                                {t("general.submit")}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
}

async function submit(request: IDSAR) : Promise<boolean> {
    const result = await sarService.submitAdditionalInfo(request);
    return result;
}

function validate(
    request: IDSAR,
    setValidation: (validation: IDSARValidation) => void
) {
    const validation: IDSARValidation = {
        purpose: request.purpose.length > 0
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        keywords: request.keywords.length > 0 && request.keywords.filter(f => !f.trim()).length === 0
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        dataSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchLocations: FormFieldValidation.NOT_VALIDATED,
        emailSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchAddress: FormFieldValidation.NOT_VALIDATED
    };

    if (request.type.toLowerCase().startsWith("data")) {
        validation.dataSearchStartDate = !!request.dataSearchStart
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;
        
        validation.dataSearchEndDate = !!request.dataSearchEnd
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;

        validation.dataSearchLocations = !!request.dataSearchLocations
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;
    } else if (request.type.toLowerCase().startsWith("email")) {
        validation.emailSearchStartDate = !!request.emailSearchStart
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;
        
        validation.emailSearchEndDate = !!request.emailSearchEnd
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;

        validation.emailSearchAddress = !!request.emailSearchAddress
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID;
    }

    setValidation(validation);

    return Object.values(validation).every(v => v === FormFieldValidation.VALID || v === FormFieldValidation.NOT_VALIDATED);
}